import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { X, Users, BarChart3, Phone, Upload, Download, FileSpreadsheet } from 'lucide-react';
import { showToast } from '../../utils/toast';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function AddPerformanceDataModal({ isOpen, onClose, clients, onSuccess }) {
  const [entryMode, setEntryMode] = useState('manual'); // manual or bulk
  const [dataType, setDataType] = useState('leads'); // leads, ads, calls
  const [loading, setLoading] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkDataType, setBulkDataType] = useState('leads');

  // Leads form
  const [leadsData, setLeadsData] = useState({
    client_id: '',
    date: '',
    source: 'Google Ads',
    total_leads: '',
    qualified_leads: '',
    booked_appointments: '',
    closed_deals: '',
    notes: ''
  });

  // Ads form
  const [adsData, setAdsData] = useState({
    client_id: '',
    platform: 'Google',
    campaign_name: '',
    start_date: '',
    end_date: '',
    impressions: '',
    clicks: '',
    cost: '',
    conversions: '',
    notes: ''
  });

  // Calls form
  const [callsData, setCallsData] = useState({
    client_id: '',
    date: '',
    total_calls: '',
    answered_calls: '',
    missed_calls: '',
    avg_call_duration_seconds: '',
    notes: ''
  });

  const resetForms = () => {
    setLeadsData({
      client_id: '',
      date: '',
      source: 'Google Ads',
      total_leads: '',
      qualified_leads: '',
      booked_appointments: '',
      closed_deals: '',
      notes: ''
    });
    setAdsData({
      client_id: '',
      platform: 'Google',
      campaign_name: '',
      start_date: '',
      end_date: '',
      impressions: '',
      clicks: '',
      cost: '',
      conversions: '',
      notes: ''
    });
    setCallsData({
      client_id: '',
      date: '',
      total_calls: '',
      answered_calls: '',
      missed_calls: '',
      avg_call_duration_seconds: '',
      notes: ''
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      let endpoint = '';
      let payload = {};

      if (dataType === 'leads') {
        if (!leadsData.client_id || !leadsData.date) {
          showToast('Please fill in required fields', 'error');
          return;
        }
        endpoint = '/api/admin/performance/leads';
        payload = {
          ...leadsData,
          total_leads: parseInt(leadsData.total_leads) || 0,
          qualified_leads: parseInt(leadsData.qualified_leads) || 0,
          booked_appointments: parseInt(leadsData.booked_appointments) || 0,
          closed_deals: parseInt(leadsData.closed_deals) || 0
        };
      } else if (dataType === 'ads') {
        if (!adsData.client_id || !adsData.start_date || !adsData.end_date) {
          showToast('Please fill in required fields', 'error');
          return;
        }
        endpoint = '/api/admin/performance/ads';
        payload = {
          ...adsData,
          impressions: parseInt(adsData.impressions) || 0,
          clicks: parseInt(adsData.clicks) || 0,
          cost: parseFloat(adsData.cost) || 0,
          conversions: parseInt(adsData.conversions) || 0
        };
      } else if (dataType === 'calls') {
        if (!callsData.client_id || !callsData.date) {
          showToast('Please fill in required fields', 'error');
          return;
        }
        endpoint = '/api/admin/performance/calls';
        payload = {
          ...callsData,
          total_calls: parseInt(callsData.total_calls) || 0,
          answered_calls: parseInt(callsData.answered_calls) || 0,
          missed_calls: parseInt(callsData.missed_calls) || 0,
          avg_call_duration_seconds: callsData.avg_call_duration_seconds 
            ? parseInt(callsData.avg_call_duration_seconds) 
            : null
        };
      }

      const response = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        showToast('Performance data added successfully', 'success');
        resetForms();
        onSuccess?.();
        onClose();
      } else {
        const error = await response.json();
        showToast(error.detail || 'Failed to add performance data', 'error');
      }
    } catch (error) {
      console.error('Error adding performance data:', error);
      showToast('Error adding performance data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkImport = async () => {
    if (!bulkFile) {
      showToast('Please select a CSV file', 'error');
      return;
    }

    try {
      setLoading(true);
      
      // Parse CSV file
      const text = await bulkFile.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        showToast('CSV file is empty or invalid', 'error');
        return;
      }

      // Parse header and rows
      const headers = lines[0].split(',').map(h => h.trim());
      const rows = lines.slice(1);
      
      // Validate headers based on data type
      const requiredHeaders = {
        leads: ['client_id', 'date', 'source', 'total_leads', 'qualified_leads', 'booked_appointments', 'closed_deals'],
        ads: ['client_id', 'platform', 'start_date', 'end_date', 'impressions', 'clicks', 'cost', 'conversions'],
        calls: ['client_id', 'date', 'total_calls', 'answered_calls', 'missed_calls']
      };
      
      const expected = requiredHeaders[bulkDataType];
      const missingHeaders = expected.filter(h => !headers.includes(h));
      
      if (missingHeaders.length > 0) {
        showToast(`Missing required columns: ${missingHeaders.join(', ')}`, 'error');
        return;
      }
      
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      
      // Process each row
      for (const row of rows) {
        const values = row.split(',').map(v => v.trim());
        
        try {
          let endpoint = '';
          let payload = {};
          
          if (bulkDataType === 'leads') {
            // Expected format: client_id,date,source,total_leads,qualified_leads,booked_appointments,closed_deals,notes
            payload = {
              client_id: values[0],
              date: values[1],
              source: values[2] || 'Other',
              total_leads: parseInt(values[3]) || 0,
              qualified_leads: parseInt(values[4]) || 0,
              booked_appointments: parseInt(values[5]) || 0,
              closed_deals: parseInt(values[6]) || 0,
              notes: values[7] || ''
            };
            endpoint = '/api/admin/performance/leads';
          } else if (bulkDataType === 'ads') {
            // Expected format: client_id,platform,campaign_name,start_date,end_date,impressions,clicks,cost,conversions,notes
            payload = {
              client_id: values[0],
              platform: values[1] || 'Other',
              campaign_name: values[2] || '',
              start_date: values[3],
              end_date: values[4],
              impressions: parseInt(values[5]) || 0,
              clicks: parseInt(values[6]) || 0,
              cost: parseFloat(values[7]) || 0,
              conversions: parseInt(values[8]) || 0,
              notes: values[9] || ''
            };
            endpoint = '/api/admin/performance/ads';
          } else if (bulkDataType === 'calls') {
            // Expected format: client_id,date,total_calls,answered_calls,missed_calls,avg_call_duration_seconds,notes
            payload = {
              client_id: values[0],
              date: values[1],
              total_calls: parseInt(values[2]) || 0,
              answered_calls: parseInt(values[3]) || 0,
              missed_calls: parseInt(values[4]) || 0,
              avg_call_duration_seconds: values[5] ? parseInt(values[5]) : null,
              notes: values[6] || ''
            };
            endpoint = '/api/admin/performance/calls';
          }

          const response = await fetch(`${API}${endpoint}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });

          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
            const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
            errors.push(`Row ${rows.indexOf(row) + 2}: ${error.detail}`);
          }
        } catch (err) {
          errorCount++;
          errors.push(`Row ${rows.indexOf(row) + 2}: ${err.message}`);
          console.error('Error processing row:', err);
        }
      }

      if (successCount > 0) {
        let message = `Successfully imported ${successCount} record${successCount > 1 ? 's' : ''}`;
        if (errorCount > 0) {
          message += `, ${errorCount} failed`;
          if (errors.length > 0 && errors.length <= 5) {
            message += `: ${errors.join('; ')}`;
          }
        }
        showToast(message, 'success');
        setBulkFile(null);
        onSuccess?.();
        if (errorCount === 0) {
          onClose();
        }
      } else {
        let message = 'Failed to import any records';
        if (errors.length > 0 && errors.length <= 5) {
          message += `: ${errors.join('; ')}`;
        }
        showToast(message, 'error');
      }
    } catch (error) {
      console.error('Error importing CSV:', error);
      showToast('Error processing CSV file', 'error');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    let csvContent = '';
    let filename = '';

    if (bulkDataType === 'leads') {
      csvContent = 'client_id,date,source,total_leads,qualified_leads,booked_appointments,closed_deals,notes\n';
      csvContent += 'BM-123456,2024-01-15,Google Ads,25,15,10,5,Sample note\n';
      filename = 'leads_template.csv';
    } else if (bulkDataType === 'ads') {
      csvContent = 'client_id,platform,campaign_name,start_date,end_date,impressions,clicks,cost,conversions,notes\n';
      csvContent += 'BM-123456,Google,Summer Campaign,2024-01-01,2024-01-31,50000,2500,1250.50,100,Sample note\n';
      filename = 'ads_template.csv';
    } else if (bulkDataType === 'calls') {
      csvContent = 'client_id,date,total_calls,answered_calls,missed_calls,avg_call_duration_seconds,notes\n';
      csvContent += 'BM-123456,2024-01-15,45,38,7,180,Sample note\n';
      filename = 'calls_template.csv';
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Add Performance Data</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Mode Selector */}
          <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
            <button
              onClick={() => setEntryMode('manual')}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                entryMode === 'manual'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Upload className="h-4 w-4 inline mr-2" />
              Manual Entry
            </button>
            <button
              onClick={() => setEntryMode('bulk')}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                entryMode === 'bulk'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileSpreadsheet className="h-4 w-4 inline mr-2" />
              Bulk CSV Import
            </button>
          </div>

          {entryMode === 'manual' && (
            <>
              {/* Data Type Selector */}
              <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => setDataType('leads')}
              className={`p-4 rounded-lg border-2 transition-all ${
                dataType === 'leads'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Users className={`h-6 w-6 mx-auto mb-2 ${dataType === 'leads' ? 'text-blue-600' : 'text-gray-400'}`} />
              <p className={`text-sm font-medium ${dataType === 'leads' ? 'text-blue-900' : 'text-gray-600'}`}>
                Leads
              </p>
            </button>

            <button
              onClick={() => setDataType('ads')}
              className={`p-4 rounded-lg border-2 transition-all ${
                dataType === 'ads'
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <BarChart3 className={`h-6 w-6 mx-auto mb-2 ${dataType === 'ads' ? 'text-purple-600' : 'text-gray-400'}`} />
              <p className={`text-sm font-medium ${dataType === 'ads' ? 'text-purple-900' : 'text-gray-600'}`}>
                Ad Performance
              </p>
            </button>

            <button
              onClick={() => setDataType('calls')}
              className={`p-4 rounded-lg border-2 transition-all ${
                dataType === 'calls'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Phone className={`h-6 w-6 mx-auto mb-2 ${dataType === 'calls' ? 'text-green-600' : 'text-gray-400'}`} />
              <p className={`text-sm font-medium ${dataType === 'calls' ? 'text-green-900' : 'text-gray-600'}`}>
                Call Tracking
              </p>
            </button>
          </div>

          {/* Leads Form */}
          {dataType === 'leads' && (
            <div className="space-y-4">
              <div>
                <Label>Client *</Label>
                <Select value={leadsData.client_id} onValueChange={(value) => setLeadsData({ ...leadsData, client_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client.client_id} value={client.client_id}>
                        {client.business_name} ({client.short_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={leadsData.date}
                    onChange={(e) => setLeadsData({ ...leadsData, date: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Source</Label>
                  <Select value={leadsData.source} onValueChange={(value) => setLeadsData({ ...leadsData, source: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Google Ads">Google Ads</SelectItem>
                      <SelectItem value="Facebook">Facebook</SelectItem>
                      <SelectItem value="Direct">Direct</SelectItem>
                      <SelectItem value="Referral">Referral</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Total Leads</Label>
                  <Input
                    type="number"
                    min="0"
                    value={leadsData.total_leads}
                    onChange={(e) => setLeadsData({ ...leadsData, total_leads: e.target.value })}
                    placeholder="0"
                  />
                </div>

                <div>
                  <Label>Qualified Leads</Label>
                  <Input
                    type="number"
                    min="0"
                    value={leadsData.qualified_leads}
                    onChange={(e) => setLeadsData({ ...leadsData, qualified_leads: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Booked Appointments</Label>
                  <Input
                    type="number"
                    min="0"
                    value={leadsData.booked_appointments}
                    onChange={(e) => setLeadsData({ ...leadsData, booked_appointments: e.target.value })}
                    placeholder="0"
                  />
                </div>

                <div>
                  <Label>Closed Deals</Label>
                  <Input
                    type="number"
                    min="0"
                    value={leadsData.closed_deals}
                    onChange={(e) => setLeadsData({ ...leadsData, closed_deals: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={leadsData.notes}
                  onChange={(e) => setLeadsData({ ...leadsData, notes: e.target.value })}
                  placeholder="Add any additional notes..."
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Ads Form */}
          {dataType === 'ads' && (
            <div className="space-y-4">
              <div>
                <Label>Client *</Label>
                <Select value={adsData.client_id} onValueChange={(value) => setAdsData({ ...adsData, client_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client.client_id} value={client.client_id}>
                        {client.business_name} ({client.short_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Platform</Label>
                  <Select value={adsData.platform} onValueChange={(value) => setAdsData({ ...adsData, platform: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Google">Google</SelectItem>
                      <SelectItem value="Meta">Meta (Facebook/Instagram)</SelectItem>
                      <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Campaign Name (Optional)</Label>
                  <Input
                    value={adsData.campaign_name}
                    onChange={(e) => setAdsData({ ...adsData, campaign_name: e.target.value })}
                    placeholder="e.g., Summer Campaign"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date *</Label>
                  <Input
                    type="date"
                    value={adsData.start_date}
                    onChange={(e) => setAdsData({ ...adsData, start_date: e.target.value })}
                  />
                </div>

                <div>
                  <Label>End Date *</Label>
                  <Input
                    type="date"
                    value={adsData.end_date}
                    onChange={(e) => setAdsData({ ...adsData, end_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Impressions</Label>
                  <Input
                    type="number"
                    min="0"
                    value={adsData.impressions}
                    onChange={(e) => setAdsData({ ...adsData, impressions: e.target.value })}
                    placeholder="0"
                  />
                </div>

                <div>
                  <Label>Clicks</Label>
                  <Input
                    type="number"
                    min="0"
                    value={adsData.clicks}
                    onChange={(e) => setAdsData({ ...adsData, clicks: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Cost ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={adsData.cost}
                    onChange={(e) => setAdsData({ ...adsData, cost: e.target.value })}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <Label>Conversions</Label>
                  <Input
                    type="number"
                    min="0"
                    value={adsData.conversions}
                    onChange={(e) => setAdsData({ ...adsData, conversions: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={adsData.notes}
                  onChange={(e) => setAdsData({ ...adsData, notes: e.target.value })}
                  placeholder="Add any additional notes..."
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Calls Form */}
          {dataType === 'calls' && (
            <div className="space-y-4">
              <div>
                <Label>Client *</Label>
                <Select value={callsData.client_id} onValueChange={(value) => setCallsData({ ...callsData, client_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client.client_id} value={client.client_id}>
                        {client.business_name} ({client.short_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={callsData.date}
                  onChange={(e) => setCallsData({ ...callsData, date: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Total Calls</Label>
                  <Input
                    type="number"
                    min="0"
                    value={callsData.total_calls}
                    onChange={(e) => setCallsData({ ...callsData, total_calls: e.target.value })}
                    placeholder="0"
                  />
                </div>

                <div>
                  <Label>Answered</Label>
                  <Input
                    type="number"
                    min="0"
                    value={callsData.answered_calls}
                    onChange={(e) => setCallsData({ ...callsData, answered_calls: e.target.value })}
                    placeholder="0"
                  />
                </div>

                <div>
                  <Label>Missed</Label>
                  <Input
                    type="number"
                    min="0"
                    value={callsData.missed_calls}
                    onChange={(e) => setCallsData({ ...callsData, missed_calls: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <Label>Avg Call Duration (seconds)</Label>
                <Input
                  type="number"
                  min="0"
                  value={callsData.avg_call_duration_seconds}
                  onChange={(e) => setCallsData({ ...callsData, avg_call_duration_seconds: e.target.value })}
                  placeholder="e.g., 180 for 3 minutes"
                />
              </div>

              <div>
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={callsData.notes}
                  onChange={(e) => setCallsData({ ...callsData, notes: e.target.value })}
                  placeholder="Add any additional notes..."
                  rows={3}
                />
              </div>
            </div>
          )}
          </>
          )}

          {entryMode === 'bulk' && (
            <div className="space-y-6">
              {/* Data Type Selector for Bulk */}
              <div>
                <Label>Data Type</Label>
                <Select value={bulkDataType} onValueChange={setBulkDataType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="leads">Leads Performance</SelectItem>
                    <SelectItem value="ads">Ad Performance</SelectItem>
                    <SelectItem value="calls">Call Tracking</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Template Download */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Download className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-medium text-blue-900 mb-1">Download CSV Template</h3>
                    <p className="text-sm text-blue-700 mb-3">
                      Download the template to see the required format and example data
                    </p>
                    <Button size="sm" variant="outline" onClick={downloadTemplate}>
                      <Download className="h-4 w-4 mr-2" />
                      Download {bulkDataType === 'leads' ? 'Leads' : bulkDataType === 'ads' ? 'Ads' : 'Calls'} Template
                    </Button>
                  </div>
                </div>
              </div>

              {/* CSV Format Guide */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">CSV Format Requirements</h3>
                {bulkDataType === 'leads' && (
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><strong>Columns:</strong> client_id, date, source, total_leads, qualified_leads, booked_appointments, closed_deals, notes</p>
                    <p><strong>Date Format:</strong> YYYY-MM-DD (e.g., 2024-01-15)</p>
                    <p><strong>Sources:</strong> Google Ads, Facebook, Direct, Referral, Other</p>
                  </div>
                )}
                {bulkDataType === 'ads' && (
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><strong>Columns:</strong> client_id, platform, campaign_name, start_date, end_date, impressions, clicks, cost, conversions, notes</p>
                    <p><strong>Date Format:</strong> YYYY-MM-DD (e.g., 2024-01-01)</p>
                    <p><strong>Platforms:</strong> Google, Meta, LinkedIn, Other</p>
                  </div>
                )}
                {bulkDataType === 'calls' && (
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><strong>Columns:</strong> client_id, date, total_calls, answered_calls, missed_calls, avg_call_duration_seconds, notes</p>
                    <p><strong>Date Format:</strong> YYYY-MM-DD (e.g., 2024-01-15)</p>
                    <p><strong>Duration:</strong> In seconds (e.g., 180 for 3 minutes)</p>
                  </div>
                )}
              </div>

              {/* File Upload */}
              <div>
                <Label>Upload CSV File</Label>
                <div className="mt-2">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setBulkFile(e.target.files[0])}
                    className="block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-lg file:border-0
                      file:text-sm file:font-medium
                      file:bg-blue-50 file:text-blue-700
                      hover:file:bg-blue-100 cursor-pointer"
                  />
                </div>
                {bulkFile && (
                  <p className="mt-2 text-sm text-green-600 flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4" />
                    {bulkFile.name}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          {entryMode === 'manual' ? (
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? 'Adding...' : 'Add Performance Data'}
            </Button>
          ) : (
            <Button onClick={handleBulkImport} disabled={loading || !bulkFile}>
              {loading ? 'Importing...' : 'Import CSV Data'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
